import React, { useCallback } from 'react';
import { FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { paperClasses, Typography, useTheme } from '@mui/material';

import {
  ProcessTalentInvitationRequestAccessMode,
  ProcessTalentInvitationResponseSendMode,
  useInviteTalentToProcess
} from '@/api/generated';
import { useSnackbar } from '@/hooks/useSnackbar';
import SettingsModalForm from '@/views/SettingsModalForm/SettingsModalForm';
import { useTalentProcessContext } from '@/views/TalentProcess/TalentProcessContext';

import InvitationModalContent from './SendInvitationContent';

const SendInvitationModal = ({ onClose }: { onClose: () => void }) => {
  const { t } = useTranslation();
  const { spacing } = useTheme();
  const { mutateAsync: inviteTalentToProcess, data: invitationResponse } = useInviteTalentToProcess();
  const { process, talent } = useTalentProcessContext();
  const { showMessage } = useSnackbar();

  const handleCloseModal = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleSend = useCallback(
    async (data: { sendMode: ProcessTalentInvitationResponseSendMode }) => {
      if (data.sendMode === ProcessTalentInvitationResponseSendMode.EMAIL && !talent.email) {
        showMessage(
          t('processes:layouts.process.sendInvitation.fieldIsEmpty', {
            fieldName: t('talents:layouts.talents.columns.email')
          })
        );

        return;
      }

      try {
        await inviteTalentToProcess({
          processId: process.id!,
          talentId: talent.id!,
          data: {
            accessMode: ProcessTalentInvitationRequestAccessMode.UNIQUE_LINK,
            sendMode: data.sendMode
          }
        });
        showMessage(
          t('processes:layouts.process.sendInvitation.successMessage', {
            firstName: talent.firstName,
            lastName: talent.lastName
          })
        );
      } catch (e) {
        showMessage(t('common:error.somethingWentWrong'));
      }
    },
    [inviteTalentToProcess, process.id, showMessage, t, talent.email, talent.firstName, talent.id, talent.lastName]
  );

  const handleCopyLink = useCallback(() => {
    if (!invitationResponse?.url) return;

    navigator.clipboard.writeText(invitationResponse?.url);
  }, [invitationResponse?.url]);

  return (
    <SettingsModalForm
      isOpen
      onClose={handleCloseModal}
      closeOnSave={false}
      onSave={handleSend as (v: FieldValues) => Promise<void>}
      shouldConfirmBeforeLeave={false}
      head={
        <Typography component="h2" variant="h3">
          {t('processes:layouts.process.sendInvitation.title')}
        </Typography>
      }
      sx={{
        [`& .${paperClasses.root}`]: {
          margin: 0,
          width: spacing(50),
          height: 'unset'
        }
      }}
    >
      <InvitationModalContent onCopyLink={handleCopyLink} />
    </SettingsModalForm>
  );
};

export default SendInvitationModal;
